/**
 * Audio element for rendering mode.
 *
 * Registers the audio asset with the composition manager so
 * the renderer knows about it for audio extraction. Uses
 * delayRender to ensure metadata is loaded.
 *
 * Written from scratch for Steve.AI.
 */

import React, {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { getAbsoluteSrc } from '../absolute-src.js';
import { CompositionManager } from '../composition-context.js';
import { useRemotionEnvironment } from '../get-environment.js';
import { random } from '../random.js';
import { SequenceContext } from '../SequenceContext.js';
import { useTimelinePosition } from '../timeline-position-state.js';
import { useCurrentFrame } from '../use-current-frame.js';
import { evaluateVolume } from '../volume-prop.js';
import { useFrameForVolumeProp } from './use-audio-frame.js';

const AudioForRenderingRefForwardingFunction = (props, ref) => {
  const audioRef = useRef(null);
  const absoluteFrame = useTimelinePosition();
  const volumePropFrame = useFrameForVolumeProp();
  const frame = useCurrentFrame();
  const sequenceContext = useContext(SequenceContext);
  const { registerAsset, unregisterAsset } = useContext(CompositionManager);
  const environment = useRemotionEnvironment();

  const id = useMemo(
    () => `audio-${random(props.src ?? '')}-${sequenceContext?.relativeFrom}-${sequenceContext?.cumulatedFrom}-${sequenceContext?.durationInFrames}`,
    [props.src, sequenceContext]
  );

  const { volume: volumeProp, playbackRate, allowAmplificationDuringRender, onDuration, ...nativeProps } = props;

  const volume = evaluateVolume({
    volume: volumeProp,
    frame: volumePropFrame,
    mediaVolume: 1,
    allowAmplificationDuringRender: allowAmplificationDuringRender ?? false,
  });

  useImperativeHandle(ref, () => audioRef.current, []);

  // Register asset for audio extraction
  useEffect(() => {
    if (!props.src) return;
    if (!window.picmaker_audioEnabled) return;
    if (props.muted || volume <= 0) return;

    registerAsset({
      type: 'audio',
      src: getAbsoluteSrc(props.src),
      id,
      frame: absoluteFrame,
      volume,
      mediaFrame: frame,
      playbackRate: props.playbackRate ?? 1,
      allowAmplificationDuringRender: allowAmplificationDuringRender ?? false,
    });
    return () => unregisterAsset(id);
  }, [props.muted, props.src, registerAsset, absoluteFrame, id, unregisterAsset, volume, volumePropFrame, frame, playbackRate, props.playbackRate, allowAmplificationDuringRender]);

  // Audio duration is not needed in rendering mode — audio-muxer.js derives all
  // timing from the project JSON. Loading <audio> metadata in headless Chromium
  // with concurrent tabs is unreliable and blocks window.ready indefinitely.

  return <audio ref={audioRef} {...nativeProps} />;
};

export const AudioForRendering = forwardRef(AudioForRenderingRefForwardingFunction);
